import React, { useState, useEffect, useContext } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'lodash';
import Loading from '../../shared/components/Loading';
import inMemoryJWT from 'services/auth/inMemoryJWT';
import { actions as userActions } from '../../redux/user';
import { actions as rolesAndRightsActions } from 'redux/rolesAndRights';
import { actions as notificationsTopbarActions } from 'redux/notificationsTopbar';
import { actions as socialProfileActions } from 'redux/socialProfiles';
import { actions as locationElocActions } from 'redux/locationEloc';
import { useSelector, useDispatch } from 'react-redux';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import config from 'config';
import { debounce } from 'lodash';
import utils from 'lib/utils';
import { getAuthCookies, clearAuthCookies } from 'lib/cookieAuth';
import { authService } from 'services';
import { loadMapplsWebSdk } from 'lib/mappls';
const { livechatUrl, notificationUrl, baseUrl, liveChatPassword } = config;

const BFCAuthProvider = (props) => {
  const dispatch = useDispatch();
  const { children } = props;
  const [loading, setLoading] = useState(true);
  const [userLoaded, setUserLoaded] = useState(true);
  const [profileLoadTimeout, setProfileLoadTimeout] = useState(false);
  const user = useSelector((state) => state.user);
  const isAuthenticated = _.get(user, 'isAuthenticated');
  const profileLoaded = _.get(user, 'profile.profileLoaded');
  const isCTUser = _.get(user, 'profile.person.is_control_tower_user') || false;
  const tokenState = useSelector((state) => state.locationEloc);
  const token = _.get(tokenState, 'entities.undefined') || '';

  const sid = _.get(user, 'social_profile_id', 0);
  const socialProfiles = useSelector((state) => state.socialProfiles);
  const profileData = _.get(socialProfiles, `data[${sid}]`) || {};
  const partnerId = _.get(profileData, 'partner_id') || null;
  const companySocialProfileId = _.get(user, 'profile.company_social_profile_id', 0);

  const personRoles = _.get(user, 'profile.person_role') || []
  const isFacilitator = personRoles.some((item) => item.role_id == 52)
  const isCTFacilitator = user?.profile?.person_role?.some(item => item?.role_code === 'CT Facilitator');
  const isCTCommercial = personRoles.some((item) => item.role_id == 23)
  const isCTSuperUser = personRoles.some((item) => item.role_id == 20)
  const userHasRights = (isFacilitator || isCTFacilitator || isCTCommercial || isCTSuperUser);


  const roles = useSelector((state) => state.rolesAndRights);

  const rolesList = roles.entities || {};
  let ct_reportcustomer_success_team = Object.values(rolesList).find(
    (x) => (x.module_name === 'Report', x.sub_module_name === 'Customer_Success_Team'),
  );
  const ct_reportcustomer_success_teamrights =
    _.get(ct_reportcustomer_success_team, `full_access`) || _.get(ct_reportcustomer_success_team, `read_access`);

  const mapplsAccessToken = config.mapplsAccessToken || '';

  //TODO : explain - doesn't work for updates - should use useSelector Hook
  //const storeState = globalThis.store.getState();
  // const userTypeEnum = _.get(storeState, 'user.profile.person.user_type_enum') || _.get(storeState, 'user.person.user_type_enum');

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      if (!isAuthenticated) {
        // First, check if auth tokens are available in cookies (for cross-portal authentication)
        try {
          const cookieAuth = getAuthCookies();
          const { token, refreshToken, tokenExpiry, pid, sid, loginStatusId } = cookieAuth;

          // Validate token expiry before using cookies
          let isValidToken = false;
          if (token && refreshToken) {
            if (tokenExpiry) {
              try {
                const expiryDate = new Date(tokenExpiry);
                const now = new Date();
                isValidToken = expiryDate > now;
              } catch (e) {
                // If we can't parse expiry, assume invalid
                isValidToken = false;
              }
            } else {
              // If no expiry, assume valid (but might be expired)
              isValidToken = true;
            }
          }

          if (token && refreshToken && isValidToken) {
            // Auto-authenticate using tokens from cookies
            try {
              await authService.autoAuthentication({
                token,
                refreshToken,
                jwtExp: tokenExpiry,
                pid,
                sid,
                login_status_id: loginStatusId,
              });

              // Check tokens after setting them
              const result = await inMemoryJWT.checkTokens();
              if (result) {
                try {
                  dispatch(
                    userActions.updateUser({
                      isAuthenticated: true,
                      ...result,
                    }),
                  );
                  setLoading(false);
                  return; // Exit early if cookie auth succeeded
                } catch (err) {
                  console.log('autoAuthentication from cookies-->failed', err);
                  // Clear invalid cookies
                  clearAuthCookies();
                }
              } else {
                // Tokens are invalid, clear cookies
                clearAuthCookies();
              }
            } catch (err) {
              console.log('Error auto-authenticating from cookies:', err);
              // Clear invalid cookies on error
              try {
                clearAuthCookies();
              } catch (clearErr) {
                console.error('Error clearing invalid cookies:', clearErr);
              }
              // Fall through to check localStorage
            }
          } else if (token && refreshToken && !isValidToken) {
            // Tokens exist but are expired, clear them
            try {
              clearAuthCookies();
            } catch (clearErr) {
              console.error('Error clearing expired cookies:', clearErr);
            }
          }

          // If cookies didn't work or weren't available, check localStorage
          const result = await inMemoryJWT.checkTokens();
          if (result) {
            try {
              dispatch(
                userActions.updateUser({
                  isAuthenticated: true,
                  ...result,
                }),
              );
            } catch (err) {
              console.log('getRefreshedToken-->failed', err);
            }
          }
        } catch (err) {
          console.log('initAuth error:', err);
          // Fallback to normal token check
          const result = await inMemoryJWT.checkTokens();
          if (result) {
            try {
              dispatch(
                userActions.updateUser({
                  isAuthenticated: true,
                  ...result,
                }),
              );
            } catch (error) {
              console.log('getRefreshedToken-->failed', error);
            }
          }
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * Keep logout state in sync across portals.
   * If cookies disappear (user logged out elsewhere), clear local session.
   *
   * IMPORTANT: This check is resilient to network issues. It only logs out if:
   * 1. Cookies are missing AND
   * 2. localStorage tokens are also missing AND
   * 3. We're online (to avoid false positives during network outages)
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let logoutCheckInterval;

    const ensureCookiesPresent = async () => {
      try {
        // Check if we're online - don't logout during network outages
        const isOnline = typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine;

        // Check cookies
        const { token, refreshToken } = getAuthCookies();
        const cookiesMissing = !token || !refreshToken;

        // If cookies are missing, verify localStorage tokens as a fallback
        // Only logout if BOTH cookies AND localStorage tokens are missing
        if (cookiesMissing) {
          try {
            // Check localStorage tokens before logging out
            const localStorageToken = await inMemoryJWT.checkTokens();

            // If localStorage has valid tokens, don't logout (might be a cookie read issue)
            if (localStorageToken) {
              console.log('Cookies missing but localStorage tokens present - not logging out (possible network/cookie issue)');
              return;
            }

            // Both cookies and localStorage tokens are missing
            // Only logout if we're online (to avoid false positives during network outages)
            if (isOnline) {
              console.log('Both cookies and localStorage tokens missing while online - logging out');
              dispatch(userActions.logout());
            } else {
              console.log('Cookies and tokens missing but offline - deferring logout until online');
            }
          } catch (storageErr) {
            // If we can't check localStorage (e.g., storage error), be conservative
            // Only logout if we're online and cookies are definitely missing
            console.error('Error checking localStorage tokens:', storageErr);
            if (isOnline && cookiesMissing) {
              console.log('Cookies missing, storage check failed, but online - logging out');
              dispatch(userActions.logout());
            }
          }
        }
      } catch (err) {
        // If cookie read fails entirely, don't logout (might be a network/security issue)
        console.error('Error checking auth cookies for logout sync:', err);
        // Don't logout on cookie read errors - could be due to network issues or browser security
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ensureCookiesPresent();
      }
    };

    window.addEventListener('focus', ensureCookiesPresent);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    logoutCheckInterval = setInterval(ensureCookiesPresent, 30000);

    // Initial check
    ensureCookiesPresent();

    return () => {
      window.removeEventListener('focus', ensureCookiesPresent);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (logoutCheckInterval) {
        clearInterval(logoutCheckInterval);
      }
    };
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(userActions.fetchPersonalDetails()).catch((err) => {
        console.log('fetchPersonalDetails error:', err);
        // Ensure loading is set to false even if profile fetch fails
        setLoading(false);
      });
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(rolesAndRightsActions.fetchList());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(locationElocActions.getOAuthToken());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if ((isCTUser || ct_reportcustomer_success_teamrights || userHasRights || partnerId) && mapplsAccessToken) {
      // const script = document.createElement('script');
      // script.src = `https://apis.mapmyindia.com/advancedmaps/v1/${token}/map_load?v=1.5&plugin=prunecluster`;
      // script.async = true;
      // document.head.appendChild(script);
      loadMapplsWebSdk();
    }
  }, [isCTUser, ct_reportcustomer_success_teamrights, userHasRights, mapplsAccessToken, partnerId]);

  const userId = _.get(user, 'userid') || 0;

  const ReceiveMessage = (message) => {
    dispatch(notificationsTopbarActions.updateNewNotificationCount(1));
    let params = {
      offset: 0,
      page_size: 5,
    };
    dispatch(notificationsTopbarActions.fetchNotification(params));
  };
  useEffect(() => {
    if (userId !== 0) {
      // const params = {
      //   userId: userId,
      // }
      // const qs = utils.getQs(params)
      const url = `${baseUrl}/BiofuelSignalR/NotificationUserHub/?userId=${userId}`
      // const url = `${baseUrl}/BiofuelSignalR/NotificationUserHub/?${qs}`
      const connect = new HubConnectionBuilder()
        .configureLogging(LogLevel.Debug)
        .withUrl(url)
        .withAutomaticReconnect()
        .build();
      connect
        .start()
        .then(() => {
          connect.invoke('GetConnectionId').then((res) => console.log('NotificationToken--->', res));
          connect.on('ReceiveMessage', (message) => ReceiveMessage(message));
        })
        .catch((error) => {
          console.error('HubConnectionBuilder', error);
        });
    }
  }, [userId]);

  useEffect(() => {
    if (sid) {
      dispatch(userActions.fetchSocialProfile(sid));
    }
  }, [sid]);

  useEffect(()=>{
    window.FreshworksWidget('hide', 'launcher');
  },[])

  useEffect(() => {
    if (companySocialProfileId) {
      dispatch(userActions.fetchSocialProfile(companySocialProfileId));
    }
  }, [companySocialProfileId]);

  // Add timeout to prevent infinite loading for profile
  useEffect(() => {
    if (isAuthenticated && !profileLoaded && !profileLoadTimeout) {
      const timeout = setTimeout(() => {
        console.warn('Profile loading timeout - allowing render to proceed');
        setProfileLoadTimeout(true);
        setLoading(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, profileLoaded, profileLoadTimeout]);

  // Reset timeout flag when profile loads successfully
  useEffect(() => {
    if (profileLoaded) {
      setProfileLoadTimeout(false);
    }
  }, [profileLoaded]);

  // Show loading during initial auth check
  if (loading) {
    return <Loading loading={loading} />;
  }

  // Show loading if authenticated but profile not yet loaded (with timeout protection above)
  // Skip this check if timeout has occurred to prevent infinite loading
  if (isAuthenticated && !profileLoaded && !profileLoadTimeout) {
    return <Loading loading={true} />;
  }

  return children;
};

// const mapStateToProps = (state) => ({
//   // user : state.user,
//   isAuthenticated: _.get(state.user, 'isAuthenticated', false),
// });

// const mapDispatchToProps = (dispatch) => ({
//   userActions: bindActionCreators(userActions, dispatch),
// });

// export default connect(mapStateToProps, mapDispatchToProps)(BFCAuthProvider);

export default BFCAuthProvider;
